import type { DocMeta } from './doc-store';
import type { RemoteDocMeta } from './remote-doc-store';

export type ReconcileResult<TMetaExtra> = { metas: DocMeta<TMetaExtra>[]; localOnly: string[] };

/** Pure merge of the server's and the local store's document lists. Shared
 *  ids take their metadata (name, extras, timestamps) from the server (the
 *  source of truth for metadata) but keep the locally-cached thumbnail blob —
 *  the server never hands back image bytes in a list response. Thumbnails come
 *  from `thumbById` (the whole local thumbnail cache), NOT from the local doc
 *  metas: a diagram that lives only on the remote has no local document record,
 *  so drawing its thumbnail from the local list would miss the blob
 *  `backfillThumbnails` cached and leave its card blank on every device that
 *  never opened it. Local-only ids pass through unchanged and are reported in
 *  `localOnly` — this function is pure and has no access to revisions or the
 *  network, so it can only flag the candidates; the caller (`list()`) decides
 *  whether each is an unmigrated phase-1 doc or a document deleted on another
 *  device. Result is sorted by updatedAt descending, matching gallery display
 *  order. */
export function reconcileDocLists<TMetaExtra>(
	remote: RemoteDocMeta<TMetaExtra>[],
	local: DocMeta<TMetaExtra>[],
	metaExtraFromRemote: (r: RemoteDocMeta<TMetaExtra>) => TMetaExtra,
	thumbById: ReadonlyMap<string, Blob>,
): ReconcileResult<TMetaExtra> {
	const remoteIds = new Set(remote.map((m) => m.id));
	const metas: DocMeta<TMetaExtra>[] = [];

	for (const r of remote) {
		metas.push({
			id: r.id,
			name: r.name,
			createdAt: Date.parse(r.createdAt),
			updatedAt: Date.parse(r.updatedAt),
			thumbnail: thumbById.get(r.id) ?? null,
			...metaExtraFromRemote(r),
		} as DocMeta<TMetaExtra>);
	}

	const localOnly: string[] = [];
	for (const l of local) {
		if (remoteIds.has(l.id)) continue;
		metas.push(l);
		localOnly.push(l.id);
	}

	metas.sort((a, b) => b.updatedAt - a.updatedAt);
	return { metas, localOnly };
}
